

import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Switch,
    FormControlLabel,
    Snackbar,
    Alert,
    Autocomplete,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";
import { useCurrencyCodes } from 'hooks/useCurrencyCodes';

const normalizeCurrency = (code) => String(code || '').trim().toUpperCase();

const emptyForm = (merchantId = '', currency = '') => ({
    name: '',
    merchant_id: merchantId ? String(merchantId) : '',
    pricing_model: 'Dated',
    currency: normalizeCurrency(currency),
    season_from: '',
    season_to: '',
    min_number: '',
    component_id: '',
    package_component_id: '',
    trip_package_id: '',
    cost: '',
    is_active: true,
    trip_option_id: '',
    rejection_reason: '',
    rejected_on: '',
    departure_date_id: '',
    status: 'Pending',
    allocation_priority: 0,
    priority: 0,
});

const NewMerchantAllocationModal = ({
    isOpen,
    onClose,
    onSave,
    defaultMerchantId = '',
    defaultCurrency = '',
    lockMerchant = false,
}) => {
    const { axiosInstance, user } = useAuth();
    const { toDropdownOptions } = useCurrencyCodes();
    const currencyOptions = useMemo(
        () => toDropdownOptions({ labelFormat: 'codeName' }),
        [toDropdownOptions]
    );
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState(() => emptyForm(defaultMerchantId, defaultCurrency));
    const [formErrors, setFormErrors] = useState({});
    const [merchants, setMerchants] = useState([]);
    const [tripLogistics, setTripLogistics] = useState([]);
    const [tripPackages, setTripPackages] = useState([]);
    const [departureDates, setDepartureDates] = useState([]);
    const [loadingMerchants, setLoadingMerchants] = useState(false);
    const [loadingComponents, setLoadingComponents] = useState(false);
    const [loadingTripOptions, setLoadingTripOptions] = useState(false);
    const [loadingDepartureDates, setLoadingDepartureDates] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    const currencyFromMerchant = (merchantList, merchantId) => {
        const merchant = (merchantList || []).find(
            (m) => String(m.id) === String(merchantId)
        );
        return normalizeCurrency(merchant?.currency);
    };

    useEffect(() => {
        if (isOpen) {
            setFormData(emptyForm(defaultMerchantId, defaultCurrency));
            setFormErrors({});
            fetchMerchants();
            fetchTripLogistics();
            fetchTripPackages();
            fetchDepartureDates();
        }
    }, [isOpen, defaultMerchantId, defaultCurrency]);

    const fetchMerchants = async () => {
        setLoadingMerchants(true);
        try {
            const response = await axiosInstance.get('/accounts/?dropdown=true');
            let merchantList = response.data || [];
            if (defaultMerchantId) {
                const exists = merchantList.some((m) => String(m.id) === String(defaultMerchantId));
                if (!exists) {
                    merchantList = [
                        { id: Number(defaultMerchantId), name: `Merchant #${defaultMerchantId}` },
                        ...merchantList,
                    ];
                }
            }
            setMerchants(merchantList);
            let merchantCurrency = currencyFromMerchant(merchantList, defaultMerchantId);
            if (defaultMerchantId && !merchantCurrency && !defaultCurrency) {
                try {
                    const accountRes = await axiosInstance.get(`/accounts/${defaultMerchantId}`);
                    merchantCurrency = normalizeCurrency(accountRes.data?.currency);
                } catch (accountError) {
                    console.error('Error fetching merchant currency:', accountError);
                }
            }
            const nextCurrency =
                normalizeCurrency(defaultCurrency) || merchantCurrency;
            if (nextCurrency) {
                setFormData((prev) => ({
                    ...prev,
                    currency: nextCurrency,
                }));
            }
        } catch (error) {
            console.error('Error fetching merchants:', error);
        } finally {
            setLoadingMerchants(false);
        }
    };

    const fetchTripLogistics = async () => {
        setLoadingComponents(true);
        try {
            const response = await axiosInstance.get('/merchant-assets/trip-logistics-options');
            setTripLogistics(response.data || []);
        } catch (error) {
            console.error('Error fetching Trip Logistics:', error);
            setTripLogistics([]);
        } finally {
            setLoadingComponents(false);
        }
    };

    const fetchTripPackages = async () => {
        setLoadingTripOptions(true);
        try {
            const response = await axiosInstance.get('/trip-packages');
            setTripPackages(response.data || []);
        } catch (error) {
            console.error('Error fetching package options:', error);
            setTripPackages([]);
        } finally {
            setLoadingTripOptions(false);
        }
    };

    const fetchDepartureDates = async () => {
        setLoadingDepartureDates(true);
        try {
            const response = await axiosInstance.get('/departure-dates/', { params: { limit: 1000 } });
            setDepartureDates(response.data || []);
        } catch (error) {
            console.error('Error fetching departure dates:', error);
        } finally {
            setLoadingDepartureDates(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        const nextValue = type === 'checkbox' ? checked : value;
        setFormData((prev) => {
            const next = { ...prev, [name]: nextValue };
            if (name === 'merchant_id') {
                const merchantCurrency = currencyFromMerchant(merchants, nextValue);
                if (merchantCurrency) {
                    next.currency = merchantCurrency;
                }
            }
            return next;
        });
        setFormErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = 'Name is required';
        if (!formData.merchant_id) errors.merchant_id = 'Merchant is required';
        if (!formData.pricing_model) errors.pricing_model = 'Pricing Model is required';
        if (!formData.currency) errors.currency = 'Currency is required';
        if (!formData.package_component_id) {
            errors.package_component_id = 'Trip Logistics is required';
        }
        if (!formData.trip_package_id) {
            errors.trip_package_id = 'Package Options is required';
        }
        if (!formData.departure_date_id) errors.departure_date_id = 'Departure Date is required';
        if (!formData.cost && formData.cost !== 0) errors.cost = 'Merchant Cost is required';
        if (formData.pricing_model === 'Dated') {
            if (!formData.season_from) errors.season_from = 'Date From is required';
            if (!formData.season_to) errors.season_to = 'Date To is required';
        }

        setFormErrors(errors);
        if (Object.keys(errors).length > 0) {
            setToast({
                open: true,
                message: 'please input required fields',
                severity: 'error',
            });
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                merchant_id: parseInt(formData.merchant_id),
                component_id: formData.component_id ? parseInt(formData.component_id) : null,
                package_component_id: formData.package_component_id
                    ? parseInt(formData.package_component_id)
                    : null,
                trip_option_id: formData.trip_option_id ? parseInt(formData.trip_option_id) : null,
                trip_package_id: formData.trip_package_id ? parseInt(formData.trip_package_id) : null,
                cost: parseFloat(formData.cost),
                min_number: formData.min_number ? parseInt(formData.min_number) : null,
                season_from: formData.pricing_model === 'Dated' ? (formData.season_from || null) : null,
                season_to: formData.pricing_model === 'Dated' ? (formData.season_to || null) : null,
                departure_date_id: formData.departure_date_id ? parseInt(formData.departure_date_id) : null,
                rejection_reason: formData.rejection_reason || null,
                rejected_on: formData.rejected_on || null,
                status: formData.status || 'Pending',
                allocation_priority: formData.allocation_priority !== ''
                    ? parseInt(formData.allocation_priority, 10)
                    : 0,
                priority: formData.priority !== '' ? parseInt(formData.priority, 10) : 0,
                created_by: user?.id,
                updated_by: user?.id
            };

            await onSave(payload);
            onClose();
            setFormData(emptyForm(defaultMerchantId, defaultCurrency));
        } catch (error) {
            console.error('Error saving Merchant Allocation:', error);
            setToast({
                open: true,
                message: error.response?.data?.detail || 'Error saving Merchant Allocation',
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const tripLogisticsOptions = useMemo(() => {
        const selectedPackageId = String(formData.trip_package_id || '');
        return (tripLogistics || [])
            .filter((item) =>
                !selectedPackageId || String(item.trip_package_id || '') === selectedPackageId
            )
            .map((item) => ({
                value: String(item.id),
                label: item.label || item.name || `Logistics #${item.id}`,
                trip_package_id: item.trip_package_id ? String(item.trip_package_id) : '',
            }));
    }, [tripLogistics, formData.trip_package_id]);

    const selectedTripLogistics =
        tripLogisticsOptions.find(
            (opt) => String(opt.value) === String(formData.package_component_id || '')
        ) ||
        (tripLogistics || [])
            .filter((item) => String(item.id) === String(formData.package_component_id || ''))
            .map((item) => ({
                value: String(item.id),
                label: item.label || item.name || `Logistics #${item.id}`,
                trip_package_id: item.trip_package_id ? String(item.trip_package_id) : '',
            }))[0] ||
        null;

    const tripPackageOptions = useMemo(
        () =>
            (tripPackages || []).map((pkg) => ({
                value: String(pkg.id),
                label: pkg.title || pkg.name || `Package #${pkg.id}`,
            })),
        [tripPackages]
    );

    const selectedTripPackage =
        tripPackageOptions.find(
            (opt) => String(opt.value) === String(formData.trip_package_id || '')
        ) || null;

    const visibleDepartureDates = useMemo(() => {
        const selectedPackageId = String(formData.trip_package_id || '');
        if (!selectedPackageId) return departureDates || [];
        return (departureDates || []).filter(
            (dd) => String(dd.package_id || dd.trip_package_id || '') === selectedPackageId
        );
    }, [departureDates, formData.trip_package_id]);

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '90%', md: '900px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Merchant Allocation</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        error={!!formErrors.name}
                                        helperText={formErrors.name}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.merchant_id} required>
                                        <InputLabel>Merchant</InputLabel>
                                        <Select
                                            name="merchant_id"
                                            value={formData.merchant_id}
                                            onChange={handleInputChange}
                                            label="Merchant"

                                            disabled={loadingMerchants || lockMerchant}
                                        >
                                            <MenuItem value=""><em>Select a Merchant</em></MenuItem>
                                            {merchants.map(m => (
                                                <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.merchant_id && <MDTypography variant="caption" color="error">{formErrors.merchant_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.pricing_model} required>
                                        <InputLabel>Pricing Model</InputLabel>
                                        <Select
                                            name="pricing_model"
                                            value={formData.pricing_model}
                                            onChange={handleInputChange}
                                            label="Pricing Model"

                                        >
                                            <MenuItem value="Dated">Dated</MenuItem>
                                            <MenuItem value="Tiered">Tiered</MenuItem>
                                            <MenuItem value="PerPax">PerPax</MenuItem>
                                            <MenuItem value="PerRoom">PerRoom</MenuItem>
                                        </Select>
                                        {formErrors.pricing_model && <MDTypography variant="caption" color="error">{formErrors.pricing_model}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={currencyOptions}
                                        value={
                                            currencyOptions.find(
                                                (option) => option.value === formData.currency
                                            ) || null
                                        }
                                        getOptionLabel={(option) => option.label || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            option.value === value.value
                                        }
                                        onChange={(event, newValue) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                currency: newValue ? newValue.value : '',
                                            }));
                                            setFormErrors((prev) => ({ ...prev, currency: '' }));
                                        }}
                                        renderOption={(props, option) => (
                                            <li {...props} key={option.value}>
                                                {option.label}
                                            </li>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Currency"
                                                required
                                                margin="normal"
                                                variant="outlined"
                                                placeholder="Select currency"
                                                error={!!formErrors.currency}
                                                helperText={formErrors.currency || ''}
                                            />
                                        )}
                                    />
                                </Grid>

                                {formData.pricing_model === 'Dated' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Date From"
                                                name="season_from"
                                                type="date"
                                                value={formData.season_from}
                                                onChange={handleInputChange}
                                                InputLabelProps={{ shrink: true }}
                                                variant="outlined"
                                                margin="normal"
                                                required
                                                error={!!formErrors.season_from}
                                                helperText={formErrors.season_from}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Date To"
                                                name="season_to"
                                                type="date"
                                                value={formData.season_to}
                                                onChange={handleInputChange}
                                                InputLabelProps={{ shrink: true }}
                                                variant="outlined"
                                                margin="normal"
                                                required
                                                error={!!formErrors.season_to}
                                                helperText={formErrors.season_to}
                                            />
                                        </Grid>
                                    </>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Requested Allocation"
                                        name="min_number"
                                        type="number"
                                        value={formData.min_number}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={tripLogisticsOptions}
                                        value={selectedTripLogistics}
                                        loading={loadingComponents}
                                        openOnFocus
                                        autoHighlight
                                        clearOnEscape
                                        onChange={(_event, next) => {
                                            setFormData((prev) => ({
                                                ...prev,
                                                package_component_id: next ? String(next.value) : '',
                                                trip_package_id: next?.trip_package_id
                                                    || prev.trip_package_id,
                                                departure_date_id:
                                                    next?.trip_package_id
                                                    && next.trip_package_id !== String(prev.trip_package_id || '')
                                                        ? ''
                                                        : prev.departure_date_id,
                                            }));
                                            setFormErrors((prev) => ({
                                                ...prev,
                                                package_component_id: '',
                                                trip_package_id: '',
                                            }));
                                        }}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            String(option?.value) === String(value?.value)
                                        }
                                        ListboxProps={{ sx: { maxHeight: 240 } }}
                                        slotProps={{
                                            popper: { sx: { zIndex: 1400 } },
                                        }}
                                        noOptionsText="No trip logistics found"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Trip Logistics"
                                                required
                                                margin="normal"
                                                variant="outlined"
                                                placeholder="Select trip logistics"
                                                error={!!formErrors.package_component_id}
                                                helperText={formErrors.package_component_id || ''}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Merchant Cost"
                                        name="cost"
                                        type="number"
                                        value={formData.cost}
                                        onChange={handleInputChange}
                                        error={!!formErrors.cost}
                                        helperText={formErrors.cost}
                                        required
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <Autocomplete
                                        options={tripPackageOptions}
                                        value={selectedTripPackage}
                                        loading={loadingTripOptions}
                                        openOnFocus
                                        autoHighlight
                                        clearOnEscape
                                        onChange={(_event, next) => {
                                            const nextPackageId = next ? String(next.value) : '';
                                            setFormData((prev) => {
                                                const currentLogistics = (tripLogistics || []).find(
                                                    (item) => String(item.id) === String(prev.package_component_id || '')
                                                );
                                                const logisticsBelongsToPackage = currentLogistics
                                                    && String(currentLogistics.trip_package_id || '') === nextPackageId;
                                                return {
                                                    ...prev,
                                                    trip_package_id: nextPackageId,
                                                    package_component_id: logisticsBelongsToPackage
                                                        ? prev.package_component_id
                                                        : '',
                                                    departure_date_id: '',
                                                };
                                            });
                                            setFormErrors((prev) => ({
                                                ...prev,
                                                trip_package_id: '',
                                                package_component_id: '',
                                                departure_date_id: '',
                                            }));
                                        }}
                                        getOptionLabel={(option) => option?.label || ''}
                                        isOptionEqualToValue={(option, value) =>
                                            String(option?.value) === String(value?.value)
                                        }
                                        ListboxProps={{ sx: { maxHeight: 240 } }}
                                        slotProps={{
                                            popper: { sx: { zIndex: 1400 } },
                                        }}
                                        noOptionsText="No package options found"
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Package Options"
                                                required
                                                margin="normal"
                                                variant="outlined"
                                                placeholder="Select a package"
                                                error={!!formErrors.trip_package_id}
                                                helperText={formErrors.trip_package_id || ''}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal" error={!!formErrors.departure_date_id} required>
                                        <InputLabel>Departure Date</InputLabel>
                                        <Select
                                            name="departure_date_id"
                                            value={formData.departure_date_id}
                                            onChange={handleInputChange}
                                            label="Departure Date"

                                            disabled={loadingDepartureDates}
                                        >
                                            <MenuItem value=""><em>Select a Departure Date</em></MenuItem>
                                            {visibleDepartureDates.map(dd => (
                                                <MenuItem key={dd.id} value={dd.id}>{dd.name || dd.departure_date || `Departure #${dd.id}`}</MenuItem>
                                            ))}
                                        </Select>
                                        {formErrors.departure_date_id && <MDTypography variant="caption" color="error">{formErrors.departure_date_id}</MDTypography>}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth variant="outlined" margin="normal">
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            name="status"
                                            value={formData.status}
                                            onChange={handleInputChange}
                                            label="Status"

                                        >
                                            <MenuItem value="Pending">Pending</MenuItem>
                                            <MenuItem value="Accepted">Accepted</MenuItem>
                                            <MenuItem value="Rejected">Rejected</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Rejection Reason"
                                        name="rejection_reason"
                                        value={formData.rejection_reason}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Rejected On"
                                        name="rejected_on"
                                        type="date"
                                        value={formData.rejected_on}
                                        onChange={handleInputChange}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Allocation Priority"
                                        name="allocation_priority"
                                        type="number"
                                        value={formData.allocation_priority}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Priority"
                                        name="priority"
                                        type="number"
                                        value={formData.priority}
                                        onChange={handleInputChange}
                                        variant="outlined"
                                        margin="normal"
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={<Switch checked={formData.is_active} name="is_active" onChange={handleInputChange} />}
                                        label="Active"
                                        sx={{ mt: 2 }}
                                    />
                                </Grid>
                            </Grid>

                            <MDBox mt={4} display="flex" justifyContent="flex-end">
                                <MDButton onClick={onClose} color="secondary" sx={{ mr: 2 }}>Cancel</MDButton>
                                <MDButton type="submit" variant="gradient" color="info" disabled={isSaving}>
                                    {isSaving ? <CircularProgress size={20} color="inherit" /> : 'Save Merchant Allocation'}
                                </MDButton>
                            </MDBox>
                        </form>
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewMerchantAllocationModal;

